import { Clipboard } from '@angular/cdk/clipboard';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, CdkDragMove, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import {
    AbstractControl,
    ControlContainer,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormGroupDirective,
} from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatAccordion } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { IdleStateTrackerService } from '@klickdata/core/application';
import { AuthService } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { HttpErrorService } from '@klickdata/core/http';
import { MediaType, RecordingOutput } from '@klickdata/core/media/src/media-type';
import { RecorderService } from '@klickdata/core/media/src/recorder.service';
import { MessageFormErrorComponent, MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import {
    AppScope,
    Resource,
    ResourceCategory,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceItem, ResourceItemData, ResourceItemService, ResourceItemTypes } from '@klickdata/core/resource-item';
import { ResourceComposerDirective } from '@klickdata/core/resource/src/resource-composer-directive';
import { ResourceBuildingItems } from '@klickdata/core/resource/src/types.enum';
import { ITextCountable, TextCountableDirective, Utils } from '@klickdata/core/util';
import moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ResourceFormPracticeControlComponent } from '../resource-form-practice-control/resource-form-practice-control.component';
import { ResourceListingSheetComponent } from '../resource-listing-sheet/resource-listing-sheet.component';
import { MaterialItemComposerDirective } from './material-item-composer-directive';
import { MaterialManagerCoreService } from './material-manager-core.service';
import { AiModelSpecs } from '@klickdata/core/resource/src/resource.model';
import { VideoLinkControlComponent } from '../video-link-control/video-link-control.component';

@Component({
    selector: 'app-materials-manager-core',
    templateUrl: './materials-manager-core.component.html',
    styleUrls: ['./materials-manager-core.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class MaterialsManagerCoreComponent extends ResourceComposerDirective implements OnInit, OnChanges {
    @ViewChildren(TextCountableDirective) textCountables: QueryList<ITextCountable>;
    @ViewChildren('materialItemComposer') materialComps: QueryList<MaterialItemComposerDirective>;
    @ViewChild('parentItemsList') parentItemsList: CdkDropList<any>;
    @ViewChild('practiceControlComponent') practiceControlComponent: ResourceFormPracticeControlComponent;
    @ViewChild(VideoLinkControlComponent) videoLinkControl: VideoLinkControlComponent;
    typeId = ResourceTypes.MATERIAL;
    @Input() active: boolean;
    @Input() isEditInBottomSheet: boolean;
    public recorderOutput: RecordingOutput;
    public showPrompter: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public disableSorting: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isDragging$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private dragHoverTimer: any = null;
    private dragHoverDelay = 1000; // 1 second delay before expanding
    private currentHoveredContainer: number | null = null;
    private currentHoveredItem: number | null = null;
    private draggedElementIndex: number | null = null;
    private isDraggedFromContainer: boolean = false;
    private isDraggingContainer: boolean = false;
    public prompt = new FormControl('');
    @ViewChild('input') input: ElementRef<HTMLInputElement>;
    @ViewChild(MatAccordion) accordion?: MatAccordion;
    public totalCharsAndWordsLength = { characters: 0, words: 0 };
    public isMobile: boolean;
    public loadingMaterialsCreation: boolean;
    public expandAll: boolean;
    @Input() public opened = -1;
    public duplicatedIndices = [];
    Utils = Utils;
    agent: AiModelSpecs;
    public resourceItems: ResourceItem[] = [];
    public sortingPrio: FormControl = new FormControl(null);
    public isPractice: FormControl = new FormControl(false);
    public practicesCategoryIds: FormControl = new FormControl([]);
    public resourcePracticeCategories: ResourceCategory[] = [];
    private dragTimeout = null;
    public canRecord$: Observable<boolean>;

    constructor(
        protected categoryService: ResourceCategoryService,
        protected auth: AuthService,
        protected parentFormDirective: FormGroupDirective,
        protected messageService: MessageService,
        public resourceService: ResourceService,
        protected itemService: ResourceItemService,
        protected fb: FormBuilder,
        protected error: HttpErrorService,
        protected clipboard: Clipboard,
        protected mobileService: MobileService,
        protected cdr: ChangeDetectorRef,
        protected element: ElementRef,
        protected recordingService: RecorderService,
        protected router: Router,
        protected bottomSheet: MatBottomSheet,
        protected route: ActivatedRoute,
        protected idleStateTracker: IdleStateTrackerService,
        protected materialManagerCoreService: MaterialManagerCoreService,
        @Inject(LOCALE_ID) protected localeId: string
    ) {
        super(
            resourceService,
            categoryService,
            auth,
            parentFormDirective,
            messageService,
            router,
            bottomSheet,
            idleStateTracker
        );
        this.canRecord$ = auth.getCustomer().pipe(map((customer) => customer.hasRecordingCapabilities));
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.resourceForm.addControl('resource_items', new FormArray([]));
        this.resourceForm.addControl('collaboration', new FormGroup({}));
        this.resourceForm.addControl('article_code', new FormControl(this.resource ? this.resource.article_code : ''));

        this.items?.valueChanges
            .pipe(
                takeUntil(this.destroy),
                debounceTime(150),
                distinctUntilChanged(),
                tap(() => this.buildTextCount())
            )
            .subscribe();
        // this.onDownloadHandler();
        this.mobileService
            .isMobile()
            .pipe(takeUntil(this.destroy))
            .subscribe((isMob) => (this.isMobile = isMob));
        this.route.queryParams
            .pipe(
                filter((params) => params.action == 'fetchRecorded'),
                takeUntil(this.destroy),
                switchMap(() => this.recordingService.getCurrentResourceId()),
                filter((id) => !id)
            )
            .subscribe(() => this.listenToRecorder());
    }
    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes.resource && this.resource?.id) {
        }
    }
    get selection(): SelectionModel<any> {
        return this.materialManagerCoreService.selection;
    }

    public addMaterial() {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_QUESTIONS,
            data: {
                type: AppScope.MATERIAL,
                buildingItems: [
                    ResourceBuildingItems.CREATE_NEW_FILE_MATERIAL,
                    ResourceBuildingItems.CREATE_NEW_LINK_MATERIAL,
                    ResourceBuildingItems.CREATE_NEW_TEXT_MATERIAL,
                    ResourceBuildingItems.CREATE_NEW_RECORD_MATERIAL,
                    ResourceBuildingItems.CREATE_AI_PROMPTER_MATERIAL,
                    ResourceBuildingItems.MEDIA_LIBRARY,
                    ResourceBuildingItems.FILE_LIBRARY,
                ],
                context: 'materialCreation',
                icon: 'add_circle_outline',
                title: $localize`Add Material`,
                resource: this.resource ?? {},
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === AppScope.MATERIAL),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data.resource) {
                    this.saved.emit(data.resource);
                } else {
                    if (data.value === ResourceBuildingItems.CREATE_NEW_RECORD_MATERIAL) {
                        this.onRecordMaterial();
                        this.cdr.markForCheck();
                    } else {
                        this.onAddNewMaterial(data);
                    }
                }
            });
    }
    public showCatgeoryResources(category: ResourceCategory) {
        this.bottomSheet.open(ResourceListingSheetComponent, {
            data: {
                id: category.id,
                label: category.title,
                scope_id: AppScope.CATEGORIES,
            },
            panelClass: 'sheet-wrapper',
        });
    }
    public editInBottomSheet() {
        this.messageService.openMessage(
            MessageSavedComponent,
            $localize`This action can be done when edit in the source, please click on 'Edit Material source' button`
        );
    }
    public onAddNewMaterial(data: any) {
        switch (data.value) {
            case ResourceBuildingItems.CREATE_NEW_FILE_MATERIAL:
                this.addNewItem({ item: { title: 'File', item_type_value: 'doc_material' } });
                break;
            case ResourceBuildingItems.CREATE_NEW_LINK_MATERIAL:
                this.addNewItem({ item: { item_type_value: 'url_material' } });
                break;

            case ResourceBuildingItems.CREATE_NEW_TEXT_MATERIAL:
                this.addNewItem({ item: { item_type_value: 'text_material' } });
                break;
            case ResourceBuildingItems.CREATE_AI_PROMPTER_MATERIAL:
                this.showPrompter.next(true);
                break;
            case ResourceBuildingItems.MEDIA_LIBRARY:
                this.initImgTB({ value: ResourceMediaTypes.FILE, media: data.data });
                break;
            case ResourceBuildingItems.FILE_LIBRARY:
                this.initImgTB({ value: ResourceMediaTypes.FILE, file: data.data });
                break;
            default:
                break;
        }
        this.cdr.markForCheck();
    }
    public onTabSelectionChange(activeIndex: number) {}

    public onRecordMaterial(): void {
        this.prepareSubmit()
            .pipe(takeUntil(this.destroy))
            .subscribe((resource) => {
                this.updateMainResource(resource);
                this.recordingService.setCurrentResourceId(resource.id);
                this.recordingService.clickRecorderOptionsBtn();
            });
    }
    public updateMainResource(resource: Resource, fromSocket = false) {
        this.updateResource(resource, fromSocket);
    }
    addLink() {
        this.addType({ type: 'url_material' });
    }
    addFile() {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        if (!this.resource?.id) this.toggleAutoSave(false);
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'cloud_upload',
                title: $localize`Add media`,
                description: $localize`Please select your preferred way to add a media.`,
                type: ResourceItemTypes.MATERIAL,
                canUploadFiles: true,
            },
        });
        this.handleMediaUpload({ type: ResourceItemTypes.MATERIAL });
    }
    addText() {
        this.addType({ type: 'text_material' });
    }

    addType(params?: { type?: string; openItem?: boolean; isParent?: boolean }) {
        if (!this.resource?.id) this.toggleAutoSave(false);
        if (this.isMobile) {
            this.accordion.closeAll();
        }
        if (params?.isParent) {
            this.addParentItem({ item_type_value: params?.type }, Math.min(...this.selectedIndices)).markAsDirty();
        } else {
            this.addNewItem({ item: { item_type_value: params?.type }, openItem: params?.openItem }).markAsDirty();
        }
        this.handleMobileScrolling();
        this.toggleAutoSave(true);
    }
    addImg(index?: number) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        if (!this.resource?.id) this.toggleAutoSave(false);
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: ResourceItemTypes.MATERIAL,
                mediaType: MediaType.IMAGE,
            },
        });
        this.handleMediaUpload({ index, type: ResourceItemTypes.MATERIAL });
    }
    addVideo(index?: number) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        if (!this.resource?.id) this.toggleAutoSave(false);
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'videocam',
                title: $localize`Add a video`,
                description: $localize`Please select your preferred way to add a video.`,
                uploadBtnLabel: $localize`Upload video`,
                uploadBtnTooltip: $localize`Upload video`,
                libraryBtnLabel: $localize`Video library`,
                libraryBtnTooltip: $localize`Add video from library`,
                aiBtnLabel: $localize`Create Video`,
                disableAI: false,
                columns: ['filename', 'min_tools'],
                type: 'videomaterial',
                mediaType: MediaType.VIDEO,
                allowedMimeType: ['video/mp4', 'video/webm', 'video/3gpp'],
            },
        });
        this.handleMediaUpload({ index, type: 'videomaterial' });
    }
    private handleMediaUpload(params?: { index?: number; type?: string }) {
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === params?.type),
                takeUntil(this.destroy)
            )
            .subscribe((data) => this.initImgTB(data, params?.index));
    }
    private initImgTB(data: any, index?: number) {
        if (data.value === ResourceMediaTypes.FILE) {
            if (data.file) {
                data.file.medias?.file?.forEach((mediaId: number) => {
                    const group = this.createItemFormGroup(
                        new ResourceItem({
                            file_media_id: mediaId,
                            name: mediaId.toString(),
                            title: data.file.title,
                        })
                    );
                    group.markAsDirty();
                    if (index) {
                        this.items.insert(index, group);
                    } else {
                        this.items.push(group);
                    }
                });

                this.items.markAsDirty();
                this.openItem();
                this.performResSubmit({ eager: 'items' });
            } else {
                const imgFormGroup = this.addNewItem({
                    item: {
                        title: data.media.filename,
                        name: data.media.path,
                        item_type_value: data.media.type,
                    },
                    index,
                });
                imgFormGroup.markAsDirty();
            }
        } else {
            this.addAIMedia(data, index);
        }
        this.toggleAutoSave(true);
    }

    addAIMedia(data: any, index?: number) {
        const prompt = data.aiText ?? '';
        const promptFormGroup = this.addNewItem({
            item: { title: prompt, item_type_value: data.mediaType },
            index,
        });
        promptFormGroup.markAsDirty();
        const promptIndex = this.items.length - 1;
        this.prepareResource([promptIndex])
            .pipe(
                takeUntil(this.destroy),
                switchMap((ids) => {
                    this.updateItemsPromptStatus(ids);
                    return this.itemService.regenerateItems(ids, {
                        body: {
                            type: data.mediaType,
                            model_name: this.agent?.value ?? '',
                        },
                    });
                })
            )
            .subscribe();
    }
    toggleAutoSave(enabled: boolean) {
        this.isAutoSave = enabled;
    }
    handleMobileScrolling() {
        if (this.isMobile) {
            const headerHeight = 110;
            const breadcrumb = 25;
            const tabsHeader = 42;
            const title = (document.querySelector('.editor-title') as HTMLElement).offsetHeight;
            const bullet = (document.querySelector('.editor-bullets') as HTMLElement).offsetHeight;
            const itemsHeader = 40;
            const items = 48 * this.items.length - 2;
            window.scrollTo({
                top: headerHeight + breadcrumb + tabsHeader + title + bullet + items,
                behavior: 'smooth',
            });
        }
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
        }
    }
    private buildTextCount() {
        // this.totalCharsAndWordsLength = this.textCountables.reduce(
        //     (acc, item) => {
        //         acc.characters += item.characters;
        //         acc.words += item.words;
        //         return acc;
        //     },
        //     { characters: 0, words: 0 }
        // );

        this.totalCharsAndWordsLength = { characters: 0, words: 0 };
        this.items.controls.forEach((control: FormGroup) => {
            const name = control.get('name')?.value;
            if (control.get('item_type_value')?.value === 'text_material' && typeof name === 'string') {
                const characters = name.length;
                const words = name.split(/\s+/).length;
                this.totalCharsAndWordsLength.characters += characters;
                this.totalCharsAndWordsLength.words += words;
            }
        });
        this.cdr.markForCheck();
    }

    get tooltipInfo() {
        return [
            $localize`Characters: ${this.totalCharsAndWordsLength.characters}`,
            $localize`words: ${this.totalCharsAndWordsLength.words}`,
        ].join('\n');
    }

    toggleAll(expandAll: boolean) {
        this.materialComps.forEach((comp) => {
            comp.expansionPanel.expanded = expandAll;
        });
    }

    addFUQ() {
        const promptFormGroup = this.addNewItem({
            item: { title: this.prompt.value, item_type_value: 'text_material' },
        });
        promptFormGroup.markAsDirty();
        this.prompt.setValue('');
        this.cleanEmptyItems();
        const promptIndex = this.items.length - 1;
        this.prepareResource([promptIndex])
            .pipe(
                takeUntil(this.destroy),
                switchMap((ids) => {
                    this.updateItemsPromptStatus(ids);
                    return this.itemService.regenerateItems(ids, {
                        body: {
                            type: 'prompt',
                            agent: this.agent,
                        },
                        params: {
                            parent: this.idsFromIndices(this.selection.selected).join(),
                        },
                    });
                })
            )
            .subscribe();
    }

    get areItemsProcessing(): boolean {
        this.materialManagerCoreService.areItemsProcessing.next(this.items?.controls?.some((ctrl) => ctrl.disabled));
        return this.materialManagerCoreService.areItemsProcessing.value;
    }

    private updateItemsPromptStatus(ids: number[]) {
        setTimeout(() => ids.forEach((id) => this.items.controls.find((group) => group.value.id === id)?.disable()));
    }

    cleanEmptyItems() {
        this.items.controls = this.items.controls.filter(
            (item) => item.value.id || item.value.name || item.value.title
        );
    }

    private prepareResource(indices: number[]): Observable<number[]> {
        const resourceItemsIsDirty = this.items.controls.some(
            (ctl) => ctl.dirty && (ctl.value.id || ctl.value.name || ctl.value.title)
        );
        const shouldPrepareSubmit = !this.resource?.id || resourceItemsIsDirty;
        const obs = shouldPrepareSubmit ? this.prepareSubmit({ eager: 'items' }) : of(this.resource);
        /**
         * Reset opened state
         * @todo Make collapse smooth
         * */
        this.resetOpened();

        return <Observable<number[]>>obs.pipe(
            tap((resource) => this.updateMainResource(resource)),
            map(() => this.idsFromIndices(indices))
        );
    }
    public prepareResourceByIds(
        ids: number[],
        areItemsDirty: boolean = false,
        resetOpened: boolean = true
    ): Observable<number[]> {
        const shouldPrepareSubmit = !this.resource?.id || areItemsDirty;
        const obs = shouldPrepareSubmit ? this.prepareSubmit({ eager: 'items' }) : of(this.resource);
        if (resetOpened) {
            /**
             * Reset opened state
             * @todo Make collapse smooth
             * */
            this.resetOpened();
        }

        return <Observable<number[]>>obs.pipe(
            tap((resource) => this.updateMainResource(resource)),
            map(() => ids)
        );
    }

    private resetOpened() {
        this.opened = -1;
        setTimeout(() => {
            this.accordion?.closeAll();
        }, 1000);
    }

    private buildSockets() {
        /**
         * Resource items socket (resource itself is handled by ResourceComposerDirective).
         * Completed items are replaced in place.
         */
        this.itemService
            .listenToResourceItemsUpdate(this.resource)
            .pipe(
                takeUntil(this.destroy),
                filter((items) => !!items.length)
            )
            .subscribe((items) => {
                this.handleResourceItems(items);
                items.forEach((item) => {
                    if (item.prompt_status == 'ERROR') {
                        this.openMessage(MessageErrorComponent);
                    }
                    this.replaceFormGroupAndOpenItem(item);
                });
                this.selection.clear();
                this.items.markAsDirty();
                this.cdr.markForCheck();
            });
    }

    private handleResourceItems(items: ResourceItem[]) {
        items.forEach((sourceItem) => {
            const existingItemIndex = this.resourceItems.findIndex((targetItem) => targetItem.id === sourceItem.id);

            if (existingItemIndex !== -1) {
                this.resourceItems[existingItemIndex] = sourceItem;
            } else {
                this.resourceItems.push(sourceItem);
            }
        });
    }

    replaceFormGroupAndOpenItem(item: ResourceItem) {
        const indexToReplace = this.items.controls.findIndex((control: FormGroup) => control.value.id === item.id);
        if (indexToReplace !== -1) {
            this.items.setControl(indexToReplace, this.createItemFormGroup(item));
            this.openItem(indexToReplace);
        }
    }

    private removeFormArrayIds(ids: number[]) {
        /** itemsToKeep */
        this.items.controls = this.items.controls.filter((item) => !ids.includes(item.value.id));
    }

    get selectedIndices(): number[] {
        return this.selection.selected;
    }

    clearSelection() {
        this.selection.clear();
    }
    clearEditMode() {
        // this.materialComps.forEach((comp) => comp.onClearEditMode.emit(true));
    }

    download(ids?: number[]) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.DOWNLOAD,
            data: {
                data: Utils.getDownloadOptions('resource_item'),
                returnValue: true,
            },
        });
        this.onDownloadHandler(ids);
    }
    private onDownloadHandler(itemsIds?: number[]) {
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                takeUntil(this.destroy)
            )
            .subscribe((result) => {
                const ids = itemsIds || this.idsFromIndices(this.selectedIndices);
                if (!Utils.isEmpty(result?.value) && !Utils.isEmpty(ids)) {
                    this.itemService.download(ids, result.value.join()).subscribe((res) => {
                        const file = new Blob([res], { type: res.type });
                        const fileUrl = URL.createObjectURL(file);
                        const anchor = document.createElement('a');
                        anchor.href = fileUrl;
                        anchor.download = `KLMS_${moment().format('yMD_hms')}`;
                        anchor.click();
                        this.clearSelection();
                    });
                }
            });
    }

    delete(indices: number[]) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        const idsToDelete = this.items.controls
            .filter((item, index) => indices.includes(index) && item.value.id)
            .map((item) => item.value.id);
        /** itemsToKeep */
        this.items.controls = this.items.controls.filter((item, index) => !indices.includes(index) || item.value.id);
        if (idsToDelete.length) {
            this.loading.emit(true);
            this.mobileService.updateSideNavSub({
                dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
                data: {
                    icon: 'delete',
                    title: $localize`Delete ${indices.length > 1 ? 'these' : 'this'} item${
                        indices.length > 1 ? 's' : ''
                    }`,
                    contentBody: $localize`You are about to delete ${indices.length > 1 ? 'these' : 'this'} item${
                        indices.length > 1 ? 's' : ''
                    }. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                    positiveBtn: $localize`Delete`,
                    negativeBtn: $localize`Cancel`,
                },
            });
            this.mobileService
                .getSideNavAction()
                .pipe(
                    filter((action) => action === SideNaveActionsTypes.POSITIVE),
                    filter((res) => !!res),
                    switchMap(() =>
                        this.itemService.destroy(idsToDelete).pipe(
                            tap(() => this.removeFormArrayIds(idsToDelete)),
                            tap(() => this.loading.emit(false))
                        )
                    ),
                    takeUntil(this.destroy)
                )
                .subscribe(() => this.clearSelection());
        } else {
            this.clearSelection();
        }
    }

    makeMaterials() {
        const ids = this.idsFromIndices(this.selection.selected).map((id) => ({ id: id }));
        this.resourceService
            .duplicate(this.resource, { resource_items: ids })
            .pipe(takeUntil(this.destroy))
            .subscribe((newResource) => {
                if (newResource) {
                    this.messageService.openMessage(MessageSavedComponent, $localize`Material created.`);
                }
            });
    }

    /**
     * Translate set of items
     * @param langId language id of the output
     * @param indices index of items to translate
     */
    translate(data: { language_id: number; indices: number[] }) {
        this.loading.emit(true);

        const itemsToTranslate: { index: number; item: ResourceItem }[] = this.items.controls
            .map((ctrl, i) => ({ control: ctrl, index: i }))
            .filter((res) => data.indices.includes(res.index))
            .map((res, i) => ({
                index: res.index + i + 1,
                item: new ResourceItem({
                    ...res.control.value,
                    language_id: data.language_id,
                    parent_id: res.control.value.id,
                }),
            }));

        itemsToTranslate.forEach((itemToTranslate) => this.duplicateItem(itemToTranslate.item, itemToTranslate.index));

        this.prepareResource(itemsToTranslate.map((item) => item.index))
            .pipe(
                switchMap((ids: number[]) => {
                    this.updateItemsPromptStatus(ids);
                    return this.itemService.translateItems(ids, data.language_id, this.agent);
                }),
                takeUntil(this.destroy)
            )
            .subscribe(() => {
                this.loading.emit(false);
                this.selection.clear();
            });
    }

    regenerate(indices: number[]) {
        if (this.isEditInBottomSheet) {
            this.editInBottomSheet();
            return;
        }
        const itemControl = this.controlsFromIndices(indices)[0];
        const resourceItem =
            indices.length > 1 ? null : this.resourceItems.find((item) => item.id === itemControl.value.id);
        if (resourceItem) {
            resourceItem.name = itemControl.value.name;
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.PROMPT_REGENERATION,
            data: {
                type: 'regenerate',
                resourceItem: resourceItem,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'regenerate'),
                tap(() => this.loading.emit(true)),
                switchMap((data) => {
                    return this.prepareResource(indices).pipe(
                        switchMap((ids: number[]) => {
                            return this.itemService.regenerateItems(ids, {
                                body: {
                                    type: 'regenerate',
                                    agent: data.llm,
                                    ...(!!data.prompt && { prompt: data.prompt }),
                                },
                            });
                        })
                    );
                }),
                takeUntil(this.destroy)
            )
            .subscribe((items: ResourceItem[]) => {
                items.forEach((item) => this.replaceItemFormGroupByParentId(item));
                this.loading.emit(false);
                this.clearSelection();
            });
    }
    private replaceItemFormGroupByParentId(item: ResourceItem) {
        const newItemCtl = this.createItemFormGroup(item);
        if (
            item.isProcessing() ||
            item.prompt_status === 'PENDING' ||
            item.prompt_status === 'PROCESSING' ||
            item.prompt_status === 'PARSING'
        ) {
            newItemCtl.disable();
        }
        if (item.prompt_status == 'ERROR') {
            this.messageService.openMessage(MessageErrorComponent);
        }
        const existingItemId = this.items.controls.findIndex((targetItem) => targetItem.value.id === item.parent_id);
        if (existingItemId !== -1) {
            this.items.controls[existingItemId] = newItemCtl;
        } else {
            this.items.controls.push(newItemCtl);
        }
    }

    /**
     * Duplicate by index.
     * @param index of the item.
     */
    duplicate(index: number) {
        const item = this.items.controls[index].value;
        this.duplicateItem(item, index + 1);
    }
    onCreateTestFromMaterial(indices?: number[]) {
        const itemIds = this.idsFromIndices(indices);
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CREATE_TEST_FROM_MATERIAL,
            data: {
                resource: this.resource,
                itemIds: itemIds,
            },
        });
        this.selection.clear();
    }

    duplicateItem(item: ResourceItem, index: number) {
        const newItemCtl = this.createItemFormGroup(item);
        this.items.insert(index, newItemCtl);
        newItemCtl.patchValue({ id: null });
        newItemCtl.markAsDirty();
    }

    replaceItem(item: ResourceItem, index: number) {
        this.items.controls[index].get('deleted').patchValue(true);
        this.duplicateItem(item, index);
    }

    /**
     * Split text into blocks
     * @param blocksCount number of blocks to split into
     * @param index index of the item to split
     */
    splitText(blocksCount: number, index: number) {
        this.loading.emit(true);
        const itemToSplitCtrl = this.items.at(index);
        const itemData: ResourceItemData = {
            title: itemToSplitCtrl.value.title || Utils.getHeadline(itemToSplitCtrl.value.name),
            item_type_value: itemToSplitCtrl.value.item_type_value,
            language_id: itemToSplitCtrl.value.language_id,
        };

        const generatedIndices = Array.from({ length: blocksCount }, (_, i) => index + i + 1);
        generatedIndices.forEach((index) => this.duplicateItem(new ResourceItem(itemData), index));

        this.prepareResource(generatedIndices)
            .pipe(
                switchMap((ids: number[]) => {
                    this.updateItemsPromptStatus(ids);
                    const parentId = this.items.at(index).value.id;
                    return this.itemService.splitBlocks([parentId], ids);
                })
            )
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                this.loading.emit(false);
                this.clearSelection();
            });
    }

    /**
     * Merge items
     * @param indices indices of the items to merge
     */
    merge(indices: number[]) {
        this.loading.emit(true);
        this.prepareResource(indices)
            .pipe(
                takeUntil(this.destroy),
                switchMap((ids: number[]) =>
                    this.itemService.mergeItems(ids).pipe(map((item) => ({ ids: ids, item: item })))
                )
            )
            .subscribe((res) => this.mergeItems(res.ids, res.item));
    }

    public mergeItems(ids: number[], item: ResourceItem) {
        this.clearSelection();
        this.loading.emit(false);
        this.removeFormArrayIds(ids);
        this.items.push(this.createItemFormGroup(item));
        this.openItem();
        this.cdr.markForCheck();
    }
    addToLesson() {
        this.loading.emit(true);
        this.prepareResource(this.selectedIndices)
            .pipe(
                takeUntil(this.destroy),
                switchMap((ids: number[]) =>
                    this.itemService
                        .createMaterialItemContainer(ids, Math.min(...this.selectedIndices))
                        .pipe(map((item) => ({ ids: ids, item: item })))
                )
            )
            .subscribe((res) => {
                this.createMaterialItemContainer(res.ids, res.item, this.selectedIndices[0]);
                this.performResSubmit({ eager: 'items' });
            });
    }
    public createMaterialItemContainer(ids: number[], item: ResourceItem, firstIndex: number) {
        this.clearSelection();
        this.loading.emit(false);
        this.removeFormArrayIds(ids);
        this.items.insert(firstIndex, this.createItemFormGroup(item));
        // this.openItem();
        this.cdr.markForCheck();
    }
    idsFromIndices(indices: number[]): number[] {
        return this.controlsFromIndices(indices).map((ctl, i) => ctl.value.id || indices[i]);
    }

    controlsFromIndices(indices: number[]): AbstractControl[] {
        return this.items.controls.filter((item, index) => indices.includes(index));
    }

    copyAll() {
        const content = this.controlsFromIndices(this.selectedIndices)
            .reduce((acc, item) => {
                acc.push(
                    new DOMParser().parseFromString(item.value.title, 'text/html').body.textContent,
                    new DOMParser().parseFromString(item.value.name, 'text/html').body.textContent
                );
                return acc;
            }, [])
            .join('\n');
        this.clipboard.copy(content);
        this.messageService.openMessage(
            MessageSavedComponent,
            $localize`Item texts have been copied to the clipboard.`
        );
        this.clearSelection();
    }

    copy(data: ResourceItemData) {
        let plainText = data.name;
        // Replace multiple spaces with a single space
        plainText = plainText.replace(/\s+/g, ' ');

        // Trim leading and trailing spaces
        plainText = plainText.trim();

        // Replace consecutive new lines with a single new line
        plainText = plainText.replace(/\n+/g, '\n');

        // Remove leading and trailing new lines
        plainText = plainText.replace(/^\n|\n$/g, '');

        // Replace HTML tags with appropriate plain text representation
        plainText = plainText.replace(/<\/?(?:p|div|h\d|ul|ol|li|br)>/gi, '\n');

        // Remove remaining HTML tags
        plainText = plainText.replace(/<[^>]+>/g, '');

        this.clipboard.copy(
            new DOMParser().parseFromString(data.title + ' ' + plainText, 'text/html').body.textContent
        );
        this.messageService.openMessage(MessageSavedComponent, $localize`Text copied to clipboard`);
    }
    public addItemsFromChildren(items: ResourceItemData[]) {
        items.forEach((item) => this.addNewItem({ item: item }));
    }
    public addNewItem(params: { item: ResourceItemData; openItem?: boolean; index?: number }): FormGroup {
        const { item, openItem = true, index } = params;
        const group = this.createItemFormGroup(new ResourceItem(item));
        if (index) {
            this.items.insert(index, group);
        } else {
            this.items.push(group);
        }
        if (openItem) {
            this.openItem(index);
        }
        return group;
    }
    public addParentItem(item: ResourceItemData = {}, index: number): FormGroup {
        const group = this.createItemFormGroup(new ResourceItem(item));
        this.items.insert(index, group);
        return group;
    }

    private openItem(index: number = -1) {
        this.opened = index !== -1 ? index : this.items.length - 1;
        // if (this.prompt.value === '') {
        //     setTimeout(() => {
        //         const element =
        //             this.element.nativeElement.getElementsByClassName('on-hover-mat-item')[
        //                 index === -1 && this.isMobile ? this.items.length - 1 : index
        //             ];

        //         if (element) {
        //             element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        //         }
        //     }, 500);
        // }
        this.cdr.markForCheck();
    }

    public onFollowUp(isFirstTrigger?: boolean) {
        this.showPrompter.next(true);
        setTimeout(() => {
            this.input?.nativeElement?.focus();
        }, 300);
    }

    private selectableIndices = () =>
        this.items.controls.reduce<number[]>(
            (acc, c, i) => (!c.disabled && c.value.item_visibility && acc.push(i), acc),
            []
        );

    isAllSelected = () => this.selectableIndices().every((i) => this.selection.isSelected(i));

    masterToggle() {
        const indices = this.selectableIndices();
        indices.length &&
            (this.isAllSelected() ? this.selection.clear() : indices.forEach((i) => this.selection.select(i)));
        this.cdr.markForCheck();
    }

    onResourceChanged(created: boolean) {
        if (created) {
            if (this.resource.items) {
                this.initResourceItems(this.resource.items);
            } else if (this.resource.items_attached && this.resource.items$) {
                this.resource.items$.pipe(takeUntil(this.destroy)).subscribe((items) => this.initResourceItems(items));
            } else {
                this.listenToRecorder();
            }
            this.buildSockets();
        } else if (this.resource.items) {
            this.initResourceItems(this.resource.items, false);
        }
    }
    initResourceItems(items: ResourceItem[], withOpen = true) {
        this.resourceItems = items;
        this.items.clear();
        items.forEach((item) => {
            const itemFormGroup = this.createItemFormGroup(item);
            if (
                item.isProcessing() ||
                item.prompt_status === 'PENDING' ||
                item.prompt_status === 'PROCESSING' ||
                item.prompt_status === 'PARSING'
            ) {
                itemFormGroup.disable();
            }
            FormHelper.resetForm(itemFormGroup);
            this.items.push(itemFormGroup);
        });
        if (withOpen) {
            // this.opened = this.opened ?? this.items.controls.findIndex((control) => control.enabled);
            this.opened = items?.length && items?.length > 1 ? -1 : 0;
        }
        this.listenToRecorder();
        this.cdr.markForCheck();
    }
    private listenToRecorder() {
        this.recordingService.recordedSub
            .asObservable()
            .pipe(
                switchMap((recorderOutput) =>
                    this.recordingService
                        .getUsingRecordSubmitStatus()
                        .pipe(switchMap((status) => (status ? of(recorderOutput) : of(null))))
                ),
                takeUntil(this.destroy),
                filter((output) => !!output)
            )
            .subscribe((output) => {
                this.resourceForm.patchValue({ title: this.resourceForm.value.title || output.title });
                this.addNewItem({ item: { item_type_value: output.file.type, content: output.file } });
                this.recordingService.setUsingRecordSubmitStatus(false);
            });
    }
    public cleanControl() {
        if (this.items) {
            this.resourceForm.removeControl('resource_items');
        }
        // Clean up any active timers
        this.clearDragHoverTimer();
    }

    public onSubmit(): void {
        this.loading.emit(true);
        this.checkSubmitValid();
    }

    checkSubmitValid(): Observable<boolean> {
        if (!this.resourceForm.valid) {
            FormHelper.markForm(this.resourceForm);
            this.openMessage(MessageFormErrorComponent);
            this.loading.emit(false);
            return of(false);
        }
        const resourceItems = this.prepareMaterialItems();
        if (this.publish && (!resourceItems || !resourceItems?.length)) {
            FormHelper.markForm(this.resourceForm);
            this.openMessage(
                MessageErrorComponent,
                $localize`You can't publish the material before adding correct items`
            );
            this.loading.emit(false);
            return of(false);
        }
        super.performResSubmit(this.publish ? { eager: 'items' } : undefined);
        return of(true);
    }

    protected storeResource(data: ResourceData, params?: { [key: string]: any }): Observable<Resource> {
        return this.resourceService.createResourceTemplate(data, params);
    }

    protected prepareForm(): Observable<ResourceData> {
        const data = this.resource
            ? this.resource.getData(this.resourceForm.value)
            : new Resource(this.resourceForm.value).getData();
        if (this.items.dirty) {
            data.resource_items = this.prepareMaterialItems();
        } else {
            delete data.resource_items;
        }
        if (!this.resource?.id) {
            data.type_id = ResourceTypes.GeneralMaterial;
        }
        if (
            !!this.practiceControlComponent &&
            this.practiceControlComponent.isPractice.value &&
            !!this.practiceControlComponent.practicesCategoryIds.value.length &&
            this.practiceControlComponent.practicesCategoryIds.dirty
        ) {
            const uniqueCatIds = !!data.category_ids?.length
                ? this.practiceControlComponent.practicesCategoryIds.value.filter(
                      (id) => !data.category_ids.includes(id)
                  )
                : this.practiceControlComponent.practicesCategoryIds.value;
            data.category_ids = [...(!!data.category_ids?.length ? data.category_ids : []), ...uniqueCatIds];
        }

        return of(data);
    }
    protected prepareMaterialItems(): ResourceItemData[] {
        return this.items.controls
            .filter(
                (ctl) =>
                    ctl.value.item_type_value !== 'doc_material' ||
                    (ctl.value.item_type_value === 'doc_material' && !!ctl.value.name)
            )
            .filter((ctl) => ctl.value.id || ctl.value.name || ctl.value.title)
            .map(this.mapFormItem);
    }
    private mapFormItem(item: AbstractControl): ResourceItemData {
        const itemData: ResourceItemData = {};
        if (item.value.id) {
            itemData.id = item.value.id;

            if (item.get('name').dirty) {
                itemData.name = item.value.name;
            }
            if (item.get('title').dirty) {
                itemData.title = item.value.title;
            }
        } else {
            // itemData.item_type_value = item.value.item_type_value;
            if (item.get('item_type_value').value) {
                itemData.item_type_value = item.value.item_type_value;
            }
            if (item.get('name').value) {
                itemData.name = item.value.name;
            }
            if (item.get('title').value) {
                itemData.title = item.value.title;
            }

            if (item.get('language_id').value) {
                itemData.language_id = item.value.language_id;
            }
            if (item.get('file_media_id')?.value) {
                itemData.file_media_id = item.value.file_media_id;
            }

            if (item.get('parent_id').value) {
                itemData.parent_id = item.value.parent_id;
            }
        }
        if (item.get('text_validation').dirty) {
            itemData.text_validation = item.value.text_validation;
        }
        if (item.get('can_download') && item.get('can_download')?.dirty) {
            itemData.can_download = item.value.can_download;
        }
        if (item.get('deleted').value) {
            itemData.deleted = item.value.deleted;
        }
        if (item.get('align')?.dirty || item.get('order')?.dirty) {
            if (!itemData.metadata) {
                itemData.metadata = {};
            }
            itemData.metadata.layout = {
                align: item.value.align,
                order: item.value.order,
            };
        }
        if (
            item.get('item_visibility')?.dirty ||
            item.get('headline_visibility')?.dirty ||
            !item.value.item_visibility ||
            !item.value.headline_visibility
        ) {
            if (!itemData.metadata) {
                itemData.metadata = {};
            }
            itemData.metadata.visibility = {
                item: item.value.item_visibility,
                headline: item.value.headline_visibility,
            };
        }
        if (item.get('children_items') && item.get('children_items')?.dirty) {
            itemData.children_items = (item.get('children_items') as FormArray).controls.map((child) => {
                const value = child.value;
                if (!value.metadata) {
                    value.metadata = {};
                }
                value.metadata.layout = {
                    align: child.value.align,
                    order: child.value.order,
                };
                value.metadata.visibility = {
                    item: child.value.item_visibility,
                    headline: child.value.headline_visibility,
                };
                delete value.order;
                delete value.align;
                delete value.item_visibility;
                delete value.headline_visibility;
                return value;
            });
            itemData.children_items = itemData.children_items.filter((ctl) => ctl.id || ctl.name || ctl.title);
        }
        return itemData;
    }

    public getResKeysValidaty(): { [key: string]: string | boolean }[] {
        return [
            {
                key: 'title',
                title: $localize`Material title`,
                hasValidValue: this.resourceForm.get('title').valid,
                mandatory: true,
            },
            {
                key: 'language_id',
                title: $localize`Material language`,
                hasValidValue: this.resourceForm.get('language_id').valid,
                mandatory: true,
            },
            {
                key: 'category_ids',
                title: $localize`Material categories`,
                hasValidValue: this.resourceForm.get('category_ids').valid,
                mandatory: true,
            },
            {
                key: 'items',
                title: $localize`Material text`,
                hasValidValue: this.items?.value !== '',
                mandatory: true,
            },
            {
                key: 'media_id',
                title: $localize`Material image`,
                hasValidValue: this.resourceForm.value.media_id,
                mandatory: false,
            },
            {
                key: 'tag_ids',
                title: $localize`Material tags`,
                hasValidValue: this.resourceForm.value.tag_ids?.length,
                mandatory: false,
            },
            {
                key: 'bullets',
                title: $localize`Material Summary`,
                hasValidValue: this.resourceForm.value.bullets,
                mandatory: false,
                info: $localize`The short summary of this test that will be displayed udner the title (max 256 characters).`,
            },
            {
                key: 'description',
                title: $localize`Material description`,
                info: $localize`A description of the test that will be visible befor you take the test.`,
                hasValidValue: this.resourceForm.value.description,
                mandatory: false,
            },
            {
                key: 'instructions',
                title: $localize`Other information`,
                hasValidValue: this.resourceForm.value.instructions,
                mandatory: false,
                info: $localize`Other information that is relevant to this test.`,
            },
        ];
    }
    onAddNewBlock(data: { index: number; blockType: string }) {
        switch (data.blockType) {
            case 'text':
                this.addNewItem({ item: { item_type_value: 'text_material' }, index: data.index + 1 });
                break;
            case 'image':
                this.addImg(data.index + 1);
                break;
            case 'file':
                this.addNewItem({ item: { item_type_value: 'file_material' }, index: data.index + 1 });
                break;
            case 'link':
                this.addNewItem({ item: { item_type_value: 'url_material' }, index: data.index + 1 });
                break;
        }
    }
    get items() {
        return this.resourceForm.get('resource_items') as FormArray;
    }

    private getMateialItemsByItemTypeValue(itemTypeValue: string): MaterialItemComposerDirective[] {
        return this.materialComps.filter((comp) => comp.itemForm.value.item_type_value == itemTypeValue);
    }

    onSortPopup(index: number, sortingPrio: number) {
        moveItemInArray(this.items.controls, index, sortingPrio);
        this.items.markAsDirty();
    }

    drop(event: CdkDragDrop<FormArray>) {
        this.handleDrop(event);
    }

    public handleDrop(event: CdkDragDrop<FormArray>) {
        console.log('🎯 Drop event received!', event);

        if (!event || !event.container || !event.previousContainer) {
            console.log('❌ Invalid drop event - missing required properties');
            return;
        }

        // Keep only basic drop handling
        this.handleBasicDrop(event);
    }

    /**
     * Basic drop handling for same/cross container operations
     */
    private handleBasicDrop(event: CdkDragDrop<FormArray>) {
        const sourceArray = event.previousContainer.data;
        const targetArray = event.container.data;
        const droppedItem = sourceArray.controls[event.previousIndex];

        console.log('📋 Drop details:', {
            sameContainer: event.previousContainer === event.container,
            previousIndex: event.previousIndex,
            currentIndex: event.currentIndex,
            sourceLength: sourceArray?.length,
            targetLength: targetArray?.length,
            droppedItemType: droppedItem?.value?.item_type_value,
            isContainer: droppedItem?.value?.item_type_value === 'item_container',
        });

        console.log('📋 Container details:', {
            previousContainer: event.previousContainer,
            container: event.container,
        });

        try {
            if (event.previousContainer === event.container) {
                // Same container - reorder within the same FormArray
                console.log('🔄 Same container reorder');
                this.handleSameContainerDrop(event);
            } else {
                // Different containers - handle drag and drop cases
                console.log('↔️ Cross container transfer');
                this.handleCrossContainerDrop(event);
            }

            // Trigger change detection to update all counts and UI
            this.cdr.markForCheck();
            this.cdr.detectChanges();
            console.log('✅ Drop completed successfully');
        } catch (error) {
            console.error('❌ Error during drag and drop operation:', error);
            // Optionally show user feedback for the error
        }
    }

    public handleSameContainerDrop(event: CdkDragDrop<FormArray>) {
        const formArray = event.container.data;
        const previousIndex = event.previousIndex;
        const currentIndex = event.currentIndex;

        console.log('🔄 Same container drop details:', {
            arrayLength: formArray.controls.length,
            previousIndex,
            currentIndex,
        });

        if (previousIndex === currentIndex) {
            console.log('⏭️ No change needed - same position');
            return; // No change needed
        }

        console.log('📝 Moving item in array...');
        // Reorder the FormArray controls directly
        moveItemInArray(formArray.controls, previousIndex, currentIndex);

        // Mark as dirty to trigger change detection
        formArray.markAsDirty();
        formArray.updateValueAndValidity();

        console.log('✅ Same container drop completed');
    }

    /**
     * Standard cross-container drop for other cases
     */
    private handleCrossContainerDrop(event: CdkDragDrop<FormArray>) {
        const sourceArray = event.previousContainer.data;
        const targetArray = event.container.data;
        const droppedItem = sourceArray.controls[event.previousIndex];

        console.log('🔄 Cross container drop:', {
            sourceArray: event.previousContainer.id,
            targetArray: event.container.id,
            droppedItemType: droppedItem.value?.item_type_value,
            allowDrop: this.isValidDropTarget(droppedItem, targetArray, event.container),
        });

        // Check if this is a valid drop target
        if (!this.isValidDropTarget(droppedItem, targetArray, event.container)) {
            console.log('❌ Invalid drop target');
            return;
        }

        // Use transferArrayItem for cross-container transfers
        transferArrayItem(sourceArray.controls, targetArray.controls, event.previousIndex, event.currentIndex);

        targetArray.markAsDirty();
        sourceArray.markAsDirty();
        targetArray.updateValueAndValidity();
        sourceArray.updateValueAndValidity();

        console.log('✅ Cross container drop completed');
    }

    /**
     * Check if the drop target is valid for the dragged item
     */
    private isValidDropTarget(droppedItem: any, targetArray: FormArray, targetContainer: any): boolean {
        const droppedItemType = droppedItem.value?.item_type_value;
        const targetContainerId = targetContainer.id || targetContainer.element?.nativeElement?.id || '';

        console.log('🎯 Validating drop target:', {
            droppedItemType,
            targetContainerId,
            targetArrayLength: targetArray?.controls?.length || 0,
        });

        // Allow all items to be dropped into main materials container
        if (targetContainerId.includes('main-materials') || targetArray === this.items) {
            return true;
        }

        // Allow non-container items to be dropped into item_container
        if (targetContainerId.includes('bundle') || targetContainerId.includes('container')) {
            // Only prevent item_container from being dropped into another item_container
            // All other item types are allowed
            return droppedItemType !== 'item_container';
        }

        // Allow dropping into specific material containers
        if (targetContainerId.includes('material-item')) {
            return true;
        }

        // Default to allowing the drop if we can't determine the target type
        return true;
    }

    get parentConnectedList(): CdkDropList<any> {
        return this.parentItemsList;
    }

    get bundleConnectedList(): CdkDropList<any>[] {
        return !this.materialComps?.length
            ? []
            : this.materialComps.map((comp) => comp.bundleItemsList)?.filter(Boolean) || [];
    }

    private createItemFormGroup(item: ResourceItem): FormGroup {
        return this.fb.group({
            id: item.id,
            name: item.name,
            title: item.title,
            updated_at: item.updated_at,
            text_validation: item.text_validation || 'empty',
            language_id: item.language_id,
            parent_id: item.parent_id,
            total_tokens: item.total_tokens,
            status: item.status,
            prompt_status: item.prompt_status,
            item_type_value: item.item_type_value,
            deleted: item.deleted,
            content: item.content,
            model_name: item.model_name,
            file_media_id: item.file_media_id,
            item_visibility: item.metadata?.visibility?.item ?? true,
            headline_visibility:
                item.metadata?.visibility?.headline ?? this.getDefaultHeadlineVisibility(item.item_type_value),
        });
    }

    public getDefaultHeadlineVisibility(itemType: string): boolean {
        if (!itemType) return false;
        if (itemType.indexOf('text') !== -1) return true;
        return false;
    }
    public resourceItemById(id: number): ResourceItem {
        return this.resourceItems.find((item) => item.id === id);
    }

    /**
     * Handle drag started event
     */
    onDragStarted(event?: any): void {
        this.isDragging$.next(true);

        // Try to determine which element is being dragged
        if (event && event.source && event.source.element) {
            const draggedElement = event.source.element.nativeElement;
            const index = draggedElement.getAttribute('data-item-index');
            const parentIndex = draggedElement.getAttribute('data-parent-index');
            const itemType = draggedElement.getAttribute('data-item-type');

            this.draggedElementIndex = index ? parseInt(index, 10) : null;
            // If parentIndex exists, this item is being dragged FROM a container
            this.isDraggedFromContainer = parentIndex !== null && parentIndex !== undefined && parentIndex !== '';

            // Check if we're dragging a container
            if (this.draggedElementIndex !== null && this.items.controls[this.draggedElementIndex]) {
                const draggedItem = this.items.controls[this.draggedElementIndex];
                this.isDraggingContainer = draggedItem.value.item_type_value === 'item_container';
            } else {
                this.isDraggingContainer = false;
            }

            console.log('🔍 Drag origin analysis:', {
                rawIndex: index,
                parsedIndex: this.draggedElementIndex,
                rawParentIndex: parentIndex,
                hasParentIndex: parentIndex !== null && parentIndex !== undefined && parentIndex !== '',
                isDraggedFromContainer: this.isDraggedFromContainer,
                isDraggingContainer: this.isDraggingContainer,
                draggedItemType:
                    this.draggedElementIndex !== null
                        ? this.items.controls[this.draggedElementIndex]?.value?.item_type_value
                        : 'unknown',
            });

            console.log('🚀 Started dragging element:', {
                index: this.draggedElementIndex,
                parentIndex,
                itemType,
                isDraggedFromContainer: this.isDraggedFromContainer,
                element: draggedElement,
                id: draggedElement.id,
                attributes: {
                    'data-item-index': draggedElement.getAttribute('data-item-index'),
                    'data-parent-index': draggedElement.getAttribute('data-parent-index'),
                    'data-item-type': draggedElement.getAttribute('data-item-type'),
                },
            });
        } else {
            console.log('⚠️ No event or source element provided for drag start');
        }

        this.cdr.markForCheck();
    }

    onDragMoved(event: CdkDragMove<FormArray>): void {
        this.handleDragHoverExpansion(event);
    }

    /**
     * Handle drag ended event
     */
    onDragEnded(): void {
        this.isDragging$.next(false);
        this.resetDragStates();
        console.log('🏁 Drag ended, cleared all states');
        this.cdr.markForCheck();
    }

    /**
     * Handle drag hover expansion for item_container and handleAddToLesson for regular items
     */
    private handleDragHoverExpansion(event: CdkDragMove<FormArray>): void {
        const pointer = event.pointerPosition;
        let hoveredContainerIndex: number | null = null;
        let hoveredItemIndex: number | null = null;

        console.log('🎯 Drag move event:', {
            pointer: pointer,
            currentHoveredContainer: this.currentHoveredContainer,
            currentHoveredItem: this.currentHoveredItem,
            isDraggedFromContainer: this.isDraggedFromContainer,
            draggedElementIndex: this.draggedElementIndex,
        });

        // Debug available elements on first call
        if (this.currentHoveredContainer === null && this.currentHoveredItem === null) {
            this.debugAvailableElements();
        }

        // Check ALL items for hover detection
        this.items.controls.forEach((control, index) => {
            console.log(`🔍 Checking item ${index} of type:`, control.value.item_type_value);

            // Try multiple selector strategies with the new attributes
            const selectors = [
                `#material-item-${index}`,
                `[data-item-index="${index}"]`,
                `[formGroupName="${index}"]`,
                `[formgroupname="${index}"]`,
            ];

            let element: Element | null = null;

            for (const selector of selectors) {
                element = this.element.nativeElement.querySelector(selector);
                if (element) {
                    console.log(`✅ Found element with selector: ${selector}`);
                    break;
                }
            }

            if (!element) {
                // Fallback: get all draggable elements and pick the right index
                const allDragElements = this.element.nativeElement.querySelectorAll('[cdkDrag]');
                element = allDragElements[index];
                console.log(`🔄 Fallback: Using element at index ${index}:`, element);
            }

            if (!element) {
                // Final fallback: query by data attribute more broadly
                element = this.element.nativeElement.querySelector(`[data-item-index="${index}"]`);
                console.log(`🔄 Final fallback for item ${index}:`, element);
            }

            if (element) {
                const rect = element.getBoundingClientRect();
                console.log(`📐 Element ${index} bounds:`, {
                    left: rect.left,
                    right: rect.right,
                    top: rect.top,
                    bottom: rect.bottom,
                    width: rect.width,
                    height: rect.height,
                });

                const isInBounds =
                    pointer.x >= rect.left &&
                    pointer.x <= rect.right &&
                    pointer.y >= rect.top &&
                    pointer.y <= rect.bottom;

                console.log(`🎯 Pointer in bounds for item ${index}:`, isInBounds);

                // Only set as hovered if it's an item_container AND we're hovering over it
                // AND (if dragging from main list, it's not the same element)
                const shouldExcludeElement = !this.isDraggedFromContainer && index === this.draggedElementIndex;

                console.log(`🔍 Hover evaluation for index ${index}:`, {
                    isInBounds,
                    itemType: control.value.item_type_value,
                    isContainer: control.value.item_type_value === 'item_container',
                    isDraggedFromContainer: this.isDraggedFromContainer,
                    draggedElementIndex: this.draggedElementIndex,
                    shouldExcludeElement,
                    willSetAsHovered:
                        isInBounds && control.value.item_type_value === 'item_container' && !shouldExcludeElement,
                });

                if (isInBounds && !shouldExcludeElement) {
                    if (control.value.item_type_value === 'item_container') {
                        // Hovering over container - for expansion
                        hoveredContainerIndex = index;
                        console.log(
                            `🎉 Found hovered container: ${index} (dragging from: ${
                                this.isDraggedFromContainer ? 'container' : 'main'
                            }, draggedIndex: ${this.draggedElementIndex})`
                        );
                    } else {
                        // Hovering over regular item
                        hoveredItemIndex = index;

                        if (this.isDraggingContainer) {
                            // Case 2: Dragging container over regular item - collect item into container
                            console.log(
                                `📦 Case 2: Container hovering over item ${index} (type: ${control.value.item_type_value})`
                            );
                        } else {
                            // Case 1: Dragging regular item over another item - addToLesson
                            console.log(
                                `📄 Case 1: Item hovering over item ${index} (type: ${control.value.item_type_value})`
                            );
                        }
                    }
                } else if (isInBounds && shouldExcludeElement) {
                    console.log(`🚫 Skipping self (dragged element): ${index}`);
                }
            } else {
                console.log(`❌ No element found for item ${index}`);
            }
        });

        console.log('🔄 Final hover states:', {
            hoveredContainerIndex,
            hoveredItemIndex,
        });

        // Handle container hover state changes (for expansion)
        if (hoveredContainerIndex !== this.currentHoveredContainer) {
            console.log(`🔄 Container hover changed from ${this.currentHoveredContainer} to ${hoveredContainerIndex}`);

            // Clear previous timer if we moved to a different container
            this.clearDragHoverTimer();
            this.currentHoveredContainer = hoveredContainerIndex;

            // Start new timer if hovering over a container
            if (hoveredContainerIndex !== null) {
                console.log(`⏰ Starting expand timer for container ${hoveredContainerIndex}`);
                this.startDragHoverTimer(hoveredContainerIndex);
            }
        }

        // Handle item hover state changes (for handleAddToLesson or collectItemIntoContainer)
        if (hoveredItemIndex !== this.currentHoveredItem) {
            console.log(`🔄 Item hover changed from ${this.currentHoveredItem} to ${hoveredItemIndex}`);

            // Clear previous timer if we moved to a different item
            this.clearDragHoverTimer();
            this.currentHoveredItem = hoveredItemIndex;

            // Start new timer if hovering over a regular item
            if (hoveredItemIndex !== null) {
                if (this.isDraggingContainer) {
                    // Case 2: Container over item - collect item into container
                    console.log(`⏰ Starting collectItemIntoContainer timer for item ${hoveredItemIndex}`);
                    this.startCollectItemTimer(hoveredItemIndex, event);
                } else {
                    // Case 1: Item over item - addToLesson
                    console.log(`⏰ Starting handleAddToLesson timer for item ${hoveredItemIndex}`);
                    this.startAddToLessonTimer(hoveredItemIndex, event);
                }
            }
        }
    }

    /**
     * Start timer for expanding container on drag hover
     */
    private startDragHoverTimer(containerIndex: number): void {
        this.dragHoverTimer = setTimeout(() => {
            this.expandContainer(containerIndex);
        }, this.dragHoverDelay);
    }

    /**
     * Start timer for handleAddToLesson on item hover
     */
    private startAddToLessonTimer(itemIndex: number, event: CdkDragMove<FormArray>): void {
        this.dragHoverTimer = setTimeout(() => {
            this.handleAddToLesson(itemIndex, event);
        }, this.dragHoverDelay);
    }

    /**
     * Start timer for collectItemIntoContainer on item hover
     */
    private startCollectItemTimer(itemIndex: number, event: CdkDragMove<FormArray>): void {
        this.dragHoverTimer = setTimeout(() => {
            this.collectItemIntoContainer(itemIndex, event);
        }, this.dragHoverDelay);
    }

    /**
     * Clear the drag hover timer
     */
    private clearDragHoverTimer(): void {
        if (this.dragHoverTimer) {
            clearTimeout(this.dragHoverTimer);
            this.dragHoverTimer = null;
        }
    }

    /**
     * Case 1: Add dragged item to lesson when held over another item
     */
    private handleAddToLesson(targetItemIndex: number, event: CdkDragMove<FormArray>): void {
        if (this.draggedElementIndex === null) {
            console.error('❌ No dragged element to add to lesson');
            return;
        }

        const draggedItem = this.items.controls[this.draggedElementIndex];
        const targetItem = this.items.controls[targetItemIndex];

        console.log('📚 Adding to lesson:', {
            draggedItemId: draggedItem.value.id,
            draggedItemType: draggedItem.value.item_type_value,
            targetItemId: targetItem.value.id,
            targetItemType: targetItem.value.item_type_value,
            draggedIndex: this.draggedElementIndex,
            targetIndex: targetItemIndex,
        });

        // Execute the addToLesson action
        this.selectedIndices.push(targetItemIndex);
        this.selectedIndices.push(this.draggedElementIndex);
        this.addToLesson();

        // Properly cancel the drag operation
        this.cancelDragOperation(event);
    }

    /**
     * Case 2: Collect item into container when container is held over item
     */
    private collectItemIntoContainer(targetItemIndex: number, event: CdkDragMove<FormArray>): void {
        if (this.draggedElementIndex === null) {
            console.error('❌ No dragged container to collect item into');
            return;
        }

        if (!this.isDraggingContainer) {
            console.error('❌ Not dragging a container, cannot collect item');
            return;
        }

        const draggedContainer = this.items.controls[this.draggedElementIndex];
        const targetItem = this.items.controls[targetItemIndex];

        if (draggedContainer.value.item_type_value !== 'item_container') {
            console.error('❌ Dragged element is not a container');
            return;
        }

        if (targetItem.value.item_type_value === 'item_container') {
            console.warn('⚠️ Cannot collect a container into another container');
            return;
        }

        console.log('📦 Collecting item into container:', {
            containerId: draggedContainer.value.id,
            containerType: draggedContainer.value.item_type_value,
            targetItemId: targetItem.value.id,
            targetItemType: targetItem.value.item_type_value,
            containerIndex: this.draggedElementIndex,
            targetIndex: targetItemIndex,
        });

        // Execute the collection operation
        this.performItemCollection(draggedContainer, targetItem, targetItemIndex);

        // Properly cancel the drag operation
        this.cancelDragOperation(event);
    }

    /**
     * Perform the actual item collection into container
     */
    private performItemCollection(container: any, targetItem: any, targetItemIndex: number): void {
        const containerComp = this.findContainerComponent(this.draggedElementIndex);
        if (!containerComp?.items) {
            console.error('Container component not found');
            return;
        }

        try {
            // Perform the transfer
            transferArrayItem(
                this.items.controls,
                containerComp.items.controls,
                targetItemIndex,
                containerComp.items.controls.length
            );

            // Update forms
            this.items.markAsDirty();
            containerComp.items.markAsDirty();

            // Single change detection
            this.cdr.detectChanges();

            console.log('Item collection successful');
        } catch (error) {
            console.error('Item collection failed:', error);
            this.messageService.openMessage(MessageErrorComponent, 'Failed to move item to container.');
        }
    }

    /**
     * Helper method to find container component
     */
    private findContainerComponent(index: number): any {
        return this.materialComps.find(
            (comp) => comp.index === index && comp.itemForm.value.item_type_value === 'item_container'
        );
    }

    /**
     * Cancel the current drag operation and reset all states - Fixed for CDK overlap issues
     */
    private cancelDragOperation(event: CdkDragMove<FormArray>): void {
        console.log('🛑 Canceling drag operation - fixing CDK overlap');

        // Step 1: Immediately stop all drag-related DOM manipulations
        this.stopCdkDragImmediately(event);

        // Step 2: Fix layout holes and overlapping elements
        this.fixLayoutIssues();

        // Step 3: Reset component states
        this.resetDragStates();

        // Step 4: Restore proper element positioning
        this.restoreElementPositioning();

        // Step 5: Final UI cleanup with proper timing
        this.performFinalUICleanup();

        console.log('✅ CDK overlap issues resolved, UI restored');
    }

    /**
     * Immediately stop CDK drag operations to prevent further DOM manipulation
     */
    private stopCdkDragImmediately(event: CdkDragMove<FormArray>): void {
        try {
            // Stop all event propagation immediately
            if (event?.event) {
                event.event.preventDefault();
                event.event.stopPropagation();
                event.event.stopImmediatePropagation();
            }

            // Get the drag reference and force stop
            const dragRef = event?.source;
            if (dragRef) {
                // Disable dragging immediately
                dragRef.disabled = true;

                // Force reset position if method exists
                if (typeof dragRef.reset === 'function') {
                    dragRef.reset();
                }

                // Clear any ongoing animations or transitions
                if (dragRef.element?.nativeElement) {
                    const element = dragRef.element.nativeElement;
                    element.style.transition = 'none';
                    element.style.transform = '';
                    element.style.position = '';
                    element.style.zIndex = '';
                    element.style.pointerEvents = '';
                }
            }
        } catch (error) {
            console.warn('Error stopping CDK drag:', error);
        }
    }

    /**
     * Fix layout holes and overlapping elements caused by CDK drag
     */
    private fixLayoutIssues(): void {
        // Remove all CDK-generated elements that cause overlap
        const problematicElements = [
            '.cdk-drag-preview',
            '.cdk-drop-list-placeholder',
            '.cdk-drag-placeholder',
            '.cdk-drag-animating',
        ];

        problematicElements.forEach((selector) => {
            const elements = document.querySelectorAll(selector);
            elements.forEach((el) => {
                // Force remove the element from DOM if it's causing issues
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            });
        });

        // Fix overlapping z-index issues
        document.querySelectorAll('.cdk-drag, .cdk-drop-list').forEach((element: HTMLElement) => {
            element.style.zIndex = '';
            element.style.position = '';
        });

        // Reset any elements that might have incorrect positioning
        document
            .querySelectorAll('[style*="position: absolute"], [style*="position: fixed"]')
            .forEach((element: HTMLElement) => {
                // Only reset if it's likely a CDK-generated positioning
                if (element.style.transform.includes('translate') || element.classList.contains('cdk-drag')) {
                    element.style.position = '';
                    element.style.top = '';
                    element.style.left = '';
                    element.style.transform = '';
                }
            });
    }

    /**
     * Restore proper element positioning and remove layout holes
     */
    private restoreElementPositioning(): void {
        // Find all draggable items and ensure they're properly positioned
        const draggableItems = document.querySelectorAll('.draggable-item, .group-block, .on-hover-mat-item');

        draggableItems.forEach((element: HTMLElement, index) => {
            // Reset any CDK-applied positioning
            element.style.transform = '';
            element.style.position = '';
            element.style.zIndex = '';
            element.style.top = '';
            element.style.left = '';
            element.style.pointerEvents = '';

            // Remove all CDK classes that might affect positioning
            const cdkClasses = [
                'cdk-drag-dragging',
                'cdk-drag-preview',
                'cdk-drag-placeholder',
                'cdk-drop-list-dragging',
                'cdk-drag-disabled',
            ];

            cdkClasses.forEach((className) => {
                element.classList.remove(className);
            });

            // Restore normal flow
            element.style.display = '';
            element.style.visibility = '';
            element.style.opacity = '';
        });

        // Fix any remaining layout holes by forcing reflow
        const containers = document.querySelectorAll('.cdk-drop-list');
        containers.forEach((container: HTMLElement) => {
            // Reset container styles
            container.style.minHeight = '';
            container.style.position = '';
            container.style.zIndex = '';

            // Remove CDK dragging classes
            container.classList.remove('cdk-drop-list-dragging');

            // Force layout recalculation
            container.style.display = 'none';
            container.offsetHeight; // Trigger reflow
            container.style.display = '';
        });
    }

    /**
     * Reset component drag states
     */
    private resetDragStates(): void {
        this.isDragging$.next(false);
        this.draggedElementIndex = null;
        this.isDraggedFromContainer = false;
        this.isDraggingContainer = false;
        this.currentHoveredContainer = null;
        this.currentHoveredItem = null;

        this.clearDragHoverTimer();

        if (this.dragTimeout) {
            clearTimeout(this.dragTimeout);
            this.dragTimeout = null;
        }
    }

    /**
     * Perform final UI cleanup with proper timing to prevent flicker
     */
    private performFinalUICleanup(): void {
        // Immediate cleanup
        this.cdr.markForCheck();

        // Next tick cleanup
        setTimeout(() => {
            this.cdr.detectChanges();
            this.ensureNoRemainingCdkElements();
        }, 0);

        // Delayed cleanup to ensure everything is settled
        setTimeout(() => {
            this.finalLayoutValidation();
            this.cdr.detectChanges();
        }, 100);

        // Re-enable CDK drag after cleanup
        setTimeout(() => {
            this.reEnableCdkDrag();
        }, 150);
    }

    /**
     * Ensure no remaining CDK elements are causing issues
     */
    private ensureNoRemainingCdkElements(): void {
        // Remove any remaining CDK-generated elements
        const remainingElements = document.querySelectorAll(
            '.cdk-drag-preview, .cdk-drop-list-placeholder, .cdk-drag-placeholder'
        );

        remainingElements.forEach((el) => {
            if (el.parentNode) {
                el.parentNode.removeChild(el);
            }
        });

        // Clean up any stray transforms or positioning
        document.querySelectorAll('[style*="transform: translate"]').forEach((el: HTMLElement) => {
            if (el.style.transform.includes('translate3d') || el.style.transform.includes('translate(')) {
                el.style.transform = el.style.transform.replace(/translate[^)]*\)/g, '').trim();
                if (!el.style.transform) {
                    el.style.transform = '';
                }
            }
        });
    }

    /**
     * Validate that layout is properly restored
     */
    private finalLayoutValidation(): void {
        // Check for any elements that might still be causing layout issues
        const problematicElements = document.querySelectorAll(
            '[style*="position: absolute"][style*="transform"], [style*="z-index: 1000"]'
        );

        problematicElements.forEach((el: HTMLElement) => {
            // Only fix if it seems to be CDK-related
            if (
                el.classList.contains('cdk-drag') ||
                el.style.transform.includes('translate') ||
                el.style.zIndex === '1000'
            ) {
                el.style.position = '';
                el.style.transform = '';
                el.style.zIndex = '';
                el.style.top = '';
                el.style.left = '';
            }
        });

        // Force a final reflow on the main container
        const mainContainer = document.querySelector('.material-accordion, .cdk-drop-list');
        if (mainContainer) {
            (mainContainer as HTMLElement).style.minHeight = 'auto';
            mainContainer.scrollTop = mainContainer.scrollTop; // Force reflow
        }
    }

    /**
     * Re-enable CDK drag functionality after cleanup
     */
    private reEnableCdkDrag(): void {
        // Find all drag elements and re-enable them
        const dragElements = document.querySelectorAll('.cdk-drag');
        dragElements.forEach((el: any) => {
            if (el._dragRef && el._dragRef.disabled) {
                el._dragRef.disabled = false;
            }
        });

        console.log('CDK drag functionality re-enabled');
    }
    /**
     * Expand/open the specified container
     */
    private expandContainer(containerIndex: number): void {
        console.log(`🔧 Expanding container at index ${containerIndex}`);

        // Set the opened state to expand the container
        this.opened = containerIndex;

        // Find and expand the corresponding material component
        const materialComp = this.materialComps.find((comp, index) => {
            return comp.index === containerIndex && comp.itemForm.value.item_type_value === 'item_container';
        });

        if (materialComp && materialComp.expansionPanel) {
            // Open the expansion panel
            materialComp.expansionPanel.open();
            console.log(`✅ Container ${containerIndex} expanded successfully`);
        }

        // Trigger change detection
        this.cdr.markForCheck();
    }

    /**
     * Check if a container is currently being hovered during drag
     */
    public isContainerHovered(index: number): boolean {
        return this.isDragging$.value && this.currentHoveredContainer === index;
    }

    /**
     * Check if container is empty and should not be draggable
     */
    isContainerEmpty(item: AbstractControl): boolean {
        if (item.value.item_type_value !== 'item_container') {
            return false;
        }
        return !item.value.children_items?.length;
    }

    /**
     * Debug method to inspect available elements
     */
    private debugAvailableElements(): void {
        console.log('🔍 Debug: Available elements in DOM:');

        // Check all draggable elements
        const dragElements = this.element.nativeElement.querySelectorAll('[cdkDrag]');
        console.log(`📱 Found ${dragElements.length} cdkDrag elements:`, dragElements);

        dragElements.forEach((el, idx) => {
            console.log(`Element ${idx}:`, {
                id: el.id,
                formGroupName: el.getAttribute('formGroupName'),
                dataItemIndex: el.getAttribute('data-item-index'),
                dataItemType: el.getAttribute('data-item-type'),
                classList: Array.from(el.classList),
            });
        });

        // Check item containers specifically
        this.items.controls.forEach((control, index) => {
            if (control.value.item_type_value === 'item_container') {
                console.log(`🏠 Container ${index} should exist with type: item_container`);
            }
        });
    }

    private scrollToOpenedPanel(): void {
        if (this.opened >= 0) {
            setTimeout(() => {
                const expansionPanels = this.element.nativeElement.querySelectorAll('mat-expansion-panel');
                if (expansionPanels && expansionPanels[this.opened]) {
                    const panel = expansionPanels[this.opened];

                    // Check if the panel is already visible in the viewport
                    const rect = panel.getBoundingClientRect();
                    const isVisible =
                        rect.top >= 0 &&
                        rect.left >= 0 &&
                        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                        rect.right <= (window.innerWidth || document.documentElement.clientWidth);

                    // Only scroll if the panel is not fully visible
                    if (!isVisible) {
                        panel.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest',
                        });
                    }
                }
            }, 150); // Increased delay to ensure the panel is fully rendered and expanded
        }
    }
}
